'use client';

/**
 * API namespace `giftCards` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L8657-L8844).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const giftCards = {
  /**
   * GET /api/market/gift-cards — paginated public gift card catalog.
   *
   * Returns `GiftCardListResponse` (`giftCards: GiftCardResponse[]` +
   * `total` + `page` + `limit`). The backend clamps `page >= 1` and
   * `limit` to `[1, 100]` (defense in depth). The backend
   * independently filters by `status='approved'` + `active=true` +
   * active seller (INV-MKT-20 — same gate as coupons + products).
   *
   * Authz: PUBLIC. 403 `MARKET_PRODUCTS_DISABLED` when the flag is off.
   *
   * Spec ref: SPEC-242 §3.5 (GET /market/gift-cards), §3.6.2 (catálogo
   *           público UI — gift cards).
   */
  async list(
    filters: {
      page?: number;
      limit?: number;
      search?: string;
      category?: string;
      subcategory?: string;
      sortBy?: 'newest' | 'price_low' | 'price_high';
    },
    signal?: AbortSignal,
  ): Promise<GiftCardListResponse> {
    const qs = new URLSearchParams();
    if (filters.page !== undefined) qs.set('page', String(filters.page));
    if (filters.limit !== undefined) qs.set('limit', String(filters.limit));
    if (filters.search && filters.search.trim().length > 0) {
      qs.set('search', filters.search.trim());
    }
    if (filters.category) qs.set('category', filters.category);
    if (filters.subcategory) qs.set('subcategory', filters.subcategory);
    if (filters.sortBy) qs.set('sortBy', filters.sortBy);
    const query = qs.toString();
    return apiFetch<GiftCardListResponse>(
      query ? `/market/gift-cards?${query}` : '/market/gift-cards',
      { auth: false, signal },
    );
  },
  /**
   * GET /api/market/gift-cards/:giftCardId — public gift card detail.
   *
   * Returns `GiftCardResponse` — a privacy-filtered view of the gift
   * card (NO `sellerId` leak beyond the snapshot; includes
   * `sellerStoreName` + `sellerStoreSlug` snapshot). Includes the
   * denomination fields (`denominationCredits`,
   * `customDenomination`, `minDenomination`, `maxDenomination`) so
   * the detail page can render the denomination picker when
   * `customDenomination=true`.
   *
   * Authz: PUBLIC. 404 if the gift card does not exist OR is not
   * `status='approved'` OR `active=false` OR the seller is inactive
   * (INV-MKT-20 — same gates as coupons). 403
   * `MARKET_PRODUCTS_DISABLED` when the flag is off.
   *
   * Spec ref: SPEC-242 §3.5 (GET /market/gift-cards/:giftCardId),
   *           §3.6.2 (catálogo público UI — detalle de gift card).
   */
  async get(
    giftCardId: string,
    signal?: AbortSignal,
  ): Promise<GiftCardResponse> {
    return apiFetch<GiftCardResponse>(
      `/market/gift-cards/${encodeURIComponent(giftCardId)}`,
      { auth: false, signal },
    );
  },
  /**
   * POST /api/market/gift-cards/:giftCardId/purchase — buyer purchases
   * a gift card (CRITICAL atomic flow — debits buyer credits). Gift
   * cards are ALWAYS gifts per SPEC-242 §3.4.2 ("Siempre es regalo:
   * SÍ") — `recipientName` + `recipientEmail` are REQUIRED on every
   * purchase (the buyer cannot buy a gift card for themselves).
   *
   * Validates the body with `giftCardPurchaseSchema` (Track C #9 —
   * Zod). The URL path's `:giftCardId` is the canonical giftCardId —
   * the backend overrides the body's `giftCardId` (defense-in-depth
   * vs a client mismatch — the schema requires `giftCardId` in the
   * body so we set it here verbatim from the URL param).
   *
   * `denominationCredits` is the denomination chosen by the buyer at
   * purchase time. If `GiftCard.customDenomination=true`, the buyer
   * can pick any value in `[minDenomination, maxDenomination]`. If
   * `customDenomination=false`, the buyer must use
   * `GiftCard.denominationCredits` (the default denomination). The
   * service layer validates this on purchase.
   *
   * `pricePaidCredits = denominationCredits` (no discount on gift
   * cards per SPEC-242 §3.3.3). The service layer enforces this
   * invariant.
   *
   * Same 2FA + idempotency + authz conventions as `coupons.purchase`
   * above — see that method's docstring for the full rationale.
   *
   * Spec ref: SPEC-242 §3.4.2 (reglas de negocio — gift cards),
   *           §3.5 (POST /market/gift-cards/:giftCardId/purchase),
   *           §3.6.2 (catálogo público UI); SPEC-NEGOCIO §9.6
   *           (INV-SEC-06 — 2FA for sensitive ops) + §10.13
   *           (Cuponera Externa).
   */
  async purchase(
    giftCardId: string,
    input: GiftCardPurchaseInput,
    options?: { idempotencyKey?: string; signal?: AbortSignal },
  ): Promise<GiftCardPurchaseResponse> {
    // The Zod schema requires `giftCardId` in the body — set it from
    // the URL param verbatim (defense-in-depth vs a client mismatch).
    const body = { ...input, giftCardId };
    return apiFetch<GiftCardPurchaseResponse>(
      `/market/gift-cards/${encodeURIComponent(giftCardId)}/purchase`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        headers: options?.idempotencyKey
          ? { 'Idempotency-Key': options.idempotencyKey }
          : undefined,
        signal: options?.signal,
      },
    );
  },
  /**
   * GET /api/market/gift-cards/my-purchases — list the authenticated
   * buyer's gift card purchases (paginated, sorted by `purchasedAt`
   * desc — newest first).
   *
   * Returns `GiftCardPurchaseListResponse` (`purchases` + `total` +
   * `page` + `limit`). Each purchase includes the snapshot fields
   * (`giftCardTitle`, `giftCardBrandName`, `redemptionInstructions`,
   * `redemptionMethod`, `denominationCredits`, etc.) so the buyer's
   * "Mis Cupones" page can render the gift card tab without an extra
   * API roundtrip.
   *
   * Authz: self only — backend verifies the purchase's `buyerUserId`
   * matches `req.user!.sub`.
   *
   * Spec ref: SPEC-242 §3.5 (GET /market/gift-cards/my-purchases),
   *           §3.6.4 (detalle gift card UI).
   */
  async myPurchases(
    filters?: { page?: number; limit?: number; status?: PurchaseStatus },
    signal?: AbortSignal,
  ): Promise<GiftCardPurchaseListResponse> {
    const qs = new URLSearchParams();
    if (filters?.page !== undefined) qs.set('page', String(filters.page));
    if (filters?.limit !== undefined) qs.set('limit', String(filters.limit));
    if (filters?.status) qs.set('status', filters.status);
    const query = qs.toString();
    return apiFetch<GiftCardPurchaseListResponse>(
      query ? `/market/gift-cards/my-purchases?${query}` : '/market/gift-cards/my-purchases',
      { signal },
    );
  },
};
